import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../../authConfig";
import { useState, useEffect, useCallback, useRef } from "react";
import "./admin.css";
import Tabs from "../Tabs/Tabs";
import { FaInfoCircle } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import Users from "./Users";
import InviteUser from "./InviteUser";
import waitonCreate from "../../../assets/load.gif";
import useDebounce from "../hooks/useDebounce";

const MainAdmin = ({ setShowWAdmin }) => {
  const ApiDomain = import.meta.env.VITE_BACKEND_API_HOST;
  const { instance, accounts } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 500);
  const searchTextRef = useRef(debouncedSearchText);
  const [customerAgreements, setCustomerAgreements] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");

  const saveData = (data) => {
    getInstance("UPDATE", data);
  };

  const deleteData = (data) => {
    getInstance("DELETE", data);
  };
  const exitWindow = (e) => {
    setShowWAdmin(false);
  };

  const fetchAgreements = useCallback(
    async (authResponse, customerId) => {
      const headers = new Headers();
      const bearer = `Bearer ${authResponse.accessToken}`;
      headers.append("Authorization", bearer);

      const options = {
        method: "GET",
        headers: headers,
      };

      const targetCustomer = customerId || selectedCustomer;
      let url = `https://${ApiDomain}/customer/${targetCustomer}/agreements`;
      try {
        const response = await fetch(url, options);
        const data = await response.json();
        if (!customerId) {
          setCustomerAgreements(data);
        }
        return data;
      } catch (error) {
        console.log("Error fetching agreements:", error);
        return [];
      }
    },
    [ApiDomain, selectedCustomer]
  );

  const getInstance = useCallback(
    (type, data) => {
      const getToken = async () => {
        try {
          return await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });
        } catch (error) {
          return await instance.acquireTokenPopup({
            ...loginRequest,
            account: accounts[0],
          });
        }
      };

      return getToken().then((response) => {
        if (type === "USERSETTINGS") {
          myFetchUserSettings(response);
        } else if (type === "SENDINVITE") {
          return sendInvite(response, data);
        } else if (type === "UPDATE") {
          updateUserSettings(response, data);
        } else if (type === "DELETE") {
          deleteUserSettings(response, data);
        } else if (type === "CUSTOMERS") {
          fetchCustomers(response);
        } else if (type === "AGREEMENTS") {
          if (data) {
            return fetchAgreements(response, data);
          } else {
            return fetchAgreements(response);
          }
        }
      });
    },
    [instance, accounts, fetchAgreements, loginRequest]
  );

  useEffect(() => {
    searchTextRef.current = debouncedSearchText;

    if (debouncedSearchText !== undefined) {
      getInstance("USERSETTINGS", "");
    }
  }, [debouncedSearchText, getInstance]);

  useEffect(() => {
    getInstance("CUSTOMERS", "");
  }, []);

  useEffect(() => {
    if (selectedCustomer && selectedCustomer !== "NA") {
      getInstance("AGREEMENTS", "");
    } else {
      setCustomerAgreements([]);
    }
  }, [selectedCustomer, getInstance]);

  function sendInvite(authResponse, data = {}) {
    const headers = new Headers();
    const bearer = `Bearer ${authResponse.accessToken}`;

    headers.append("Authorization", bearer);

    const options = {
      method: "GET",
      headers: headers,
    };

    const { name, email, phone, customerId, agreements } = data;
    let url = `https://${ApiDomain}/b2c/b2c/invite?name=${name}&email=${email}&phone=${phone}`;

    // Return the promise chain to be awaited by the caller
    return fetch(url, options)
      .then((response) => {
        if (response.ok) {
          // Add user settings after successful invite
          addUserSettings(authResponse, {
            name,
            email,
            customerId,
            agreements,
          });
          return "Inbjudning har skickats.";
        } else {
          console.log(response.status, response.statusText);
          return "Skicka inbjudning misslyckades. Kontakta support.";
        }
      })
      .catch((error) => {
        console.log("error -->");
        console.log(error);
        return "Skicka inbjudning misslyckades. Kontakta support.";
      });
  }

  function addUserSettings(response, data = {}) {
    const { name, email, customerId, agreements } = data;
    const headers = new Headers();
    const bearer = `Bearer ${response.accessToken}`;

    headers.append("Authorization", bearer);

    const options = {
      method: "POST",
      headers: headers,
    };

    let url = `https://${ApiDomain}/usersettings/user?userName=${email}&customerId=${customerId}&agreements=${agreements}&fullName=${name}`;
    return fetch(url, options)
      .then((response) => response.json())
      .then((data) => {
        return data;
      })
      .catch((error) => {
        console.log("Error adding user settings:", error);
        throw error;
      });
  }

  function updateUserSettings(response, data) {
    const headers = new Headers();
    const bearer = `Bearer ${response.accessToken}`;

    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");
    const options = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(data),
    };

    let url = "";
    url = `https://${ApiDomain}/usersettings/user/update`;
    const res = fetch(url, options)
      .then((response) => response.json())
      .then((data) => {
        getInstance("USERSETTINGS", "");
      })
      .catch((error) => console.log(error));
  }

  function deleteUserSettings(response, data) {
    const headers = new Headers();
    const bearer = `Bearer ${response.accessToken}`;

    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");
    const options = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(data),
    };

    let url = "";
    url = `https://${ApiDomain}/usersettings/user/delete`;
    const res = fetch(url, options)
      .then((response) => response.json())
      .then((data) => {
        getInstance("USERSETTINGS", "");
      })
      .catch((error) => console.log(error));
  }

  function myFetchUserSettings(response) {
    const headers = new Headers();
    const bearer = `Bearer ${response.accessToken}`;
    headers.append("Authorization", bearer);

    const options = {
      method: "GET",
      headers: headers,
    };

    const url = `https://${ApiDomain}/UserSettings/get/users/settings?searchText=${searchTextRef.current}`;

    const res = fetch(url, options)
      .then((response) => response.json())
      .then((data) => {
        setUsers(data);
      })
      .catch((error) => console.log(error));
  }

  function fetchCustomers(authResponse) {
    const headers = new Headers();
    const bearer = `Bearer ${authResponse.accessToken}`;
    headers.append("Authorization", bearer);

    const options = {
      method: "GET",
      headers: headers,
    };

    let url = `https://${ApiDomain}/customer`;
    fetch(url, options)
      .then((response) => response.json())
      .then((data) => {
        setCustomers(data);
      })
      .catch((error) => console.log("Error fetching customers:", error));
  }

  return (
    <div className="divMainWindow">
      <div className="divMainHeader">
        <label className="labelMainStep">ADMIN SIDA</label>
      </div>
      <div className="divMainMiddle">
        <Tabs>
          <div label="Användarinställningar">
            <div className="divTab">
              Sök användare{" "}
              <input
                type="text"
                className="txtAdmin"
                onChange={(e) => setSearchText(e.target.value)}
              />
              <FaInfoCircle
                id={`userinfo`}
                data-tooltip-html="Lägg till nytt avtal genom att trycka på gröna plustecknet.<br />Ta bort rad görs på röda krysset."
              />
              <Tooltip
                anchorId={`userinfo`}
                multiline={true}
                data-html={true}
              />
              <Users
                users={users}
                setUsers={setUsers}
                saveData={saveData}
                deleteData={deleteData}
                customers={customers}
                agreements={customerAgreements}
                setSelectedCustomer={setSelectedCustomer}
                getInstance={getInstance}
              />
            </div>
          </div>
          <div label="Bjud in användare">
            <InviteUser getInstance={getInstance} customers={customers} />
          </div>
        </Tabs>
      </div>
      <div className="divMainFooter">
        <button className="btn btnEnd" onClick={exitWindow}>
          Stäng
        </button>
      </div>
    </div>
  );
};

export default MainAdmin;
